/**
 * ResponsiveModal — viewport-aware modal wrapper for M3+ adoption.
 *
 * Below md: (< 768px)
 *   Renders the existing Vaul Drawer (bottom-sheet) from drawer.jsx.
 *   Body is scrollable; DrawerHeader / DrawerFooter are exposed.
 *
 * md: and above
 *   Renders the existing Radix Dialog from dialog.jsx, height-capped at
 *   90vh as a flex column (REPORT-UX1) so tall content scrolls inside
 *   the dialog instead of growing past the viewport. Width, padding,
 *   animation and overlay classes are unchanged; callers can override
 *   the defaults through contentClassName (twMerge semantics).
 *
 * This file is plumbing only for M2: it has to compile and be
 * importable. M3+ migrates page-level modal call sites incrementally.
 *
 * The breakpoint switch is driven by useIsMobile() which listens to
 * `(max-width: 767px)` via window.matchMedia, matching the locked
 * breakpoint table in CLAUDE.md / RESPONSIVE_PATTERNS.md.
 */
import React from "react";
import { cn } from "../../lib/utils";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
} from "./dialog";
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerFooter,
  DrawerTitle,
  DrawerDescription,
} from "./drawer";
import { useIsMobile } from "../../hooks/useIsMobile";

/**
 * @param {{
 *   open: boolean,
 *   onOpenChange: (open: boolean) => void,
 *   title?: React.ReactNode,
 *   description?: React.ReactNode,
 *   children: React.ReactNode,
 *   footer?: React.ReactNode,
 *   contentClassName?: string,
 * }} props
 */
export function ResponsiveModal({
  open,
  onOpenChange,
  title,
  description,
  children,
  footer,
  contentClassName,
}) {
  const isMobile = useIsMobile();

  if (isMobile) {
    return (
      <Drawer open={open} onOpenChange={onOpenChange}>
        <DrawerContent className={contentClassName}>
          {(title || description) && (
            <DrawerHeader>
              {title ? <DrawerTitle>{title}</DrawerTitle> : null}
              {description ? <DrawerDescription>{description}</DrawerDescription> : null}
            </DrawerHeader>
          )}
          <div className="flex-1 overflow-y-auto px-4 pb-4">{children}</div>
          {footer ? <DrawerFooter>{footer}</DrawerFooter> : null}
        </DrawerContent>
      </Drawer>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {/* REPORT-UX1: the desktop dialog is height-capped and becomes a
          flex column, mirroring TaskFormModal's proven pattern on the
          same primitive — a tall form (the report form with its link
          pickers expanded) previously grew past the viewport in both
          directions, and the footer was only reachable by zooming the
          browser out. 90vh is RELATIVE, so it follows the real viewport
          at every zoom level; the mobile drawer branch above always had
          its own scroll region. ``cn`` lets a caller still override via
          contentClassName (twMerge keeps the last conflicting class).
          The children wrapper passes the height constraint DOWN
          (min-h-0 flex-1) instead of scrolling itself, so a form inside
          can keep its own footer visible and scroll only its fields. */}
      <DialogContent
        className={cn("flex max-h-[90vh] flex-col", contentClassName)}
      >
        {(title || description) && (
          <DialogHeader className="shrink-0">
            {title ? <DialogTitle>{title}</DialogTitle> : null}
            {description ? <DialogDescription>{description}</DialogDescription> : null}
          </DialogHeader>
        )}
        <div className="flex min-h-0 flex-1 flex-col">{children}</div>
        {footer ? <DialogFooter className="shrink-0">{footer}</DialogFooter> : null}
      </DialogContent>
    </Dialog>
  );
}

export default ResponsiveModal;
